import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { BlockPalette } from '../components/mail/BlockPalette';
import { BlockCanvas } from '../components/mail/BlockCanvas';
import { BlockEditor } from '../components/mail/BlockEditor';
import { EmailPreview } from '../components/mail/EmailPreview';
import { useMailTemplate } from '../hooks/useMailTemplates';
import { mailTemplatesService } from '../services/mail-templates.service';
import { useProduct } from '../contexts/ProductContext';
import { useEmailTheme } from '../components/mail/emailTheme';

let blockCounter = 0;

function generateBlockId(): string {
  blockCounter += 1;
  return `block-${Date.now()}-${blockCounter}`;
}

function getDefaultBlockData(type: string): Record<string, any> {
  switch (type) {
    case 'header':
      return { title: 'Título do E-mail', logoUrl: '' };
    case 'subtitle':
      return { text: 'Subtítulo', color: '#111827' };
    case 'text':
      return { content: '<p>Digite seu texto aqui...</p>' };
    case 'image':
      return { url: '', alt: '' };
    case 'cta':
      return { text: 'Clique aqui', url: 'https://', color: '#2563eb' };
    case 'divider':
      return { color: '#e5e7eb', thickness: 1 };
    case 'footer':
      return { content: '<p>© 2024 Sua Empresa. Todos os direitos reservados.</p>' };
    case 'window':
      return {
        title: 'CHANGELOG.TXT',
        content: '<p>Digite seu texto aqui...</p>',
        terminal: 'visual: atualizado\nrecursos: os mesmos',
      };
    default:
      return {};
  }
}

function createBlock(type: string): any {
  return {
    id: generateBlockId(),
    type,
    ...getDefaultBlockData(type),
  };
}

export function MailTemplateEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { selectedProductId } = useProduct();

  const isNew = id === 'new';
  const templateId = isNew ? null : Number(id);

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [skipLeadMovement, setSkipLeadMovement] = useState(false);
  const [blocks, setBlocks] = useState<any[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  const { data: existingTemplate, isLoading: isLoadingTemplate } = useMailTemplate(
    templateId as number
  );
  // Template existente segue o tema do produto dono; novo, o do produto selecionado.
  const templateProductId: number | null = existingTemplate?.productId ?? selectedProductId;
  const emailTheme = useEmailTheme(templateProductId);

  useEffect(() => {
    if (existingTemplate) {
      setName(existingTemplate.name || '');
      setSubject(existingTemplate.subject || '');
      setSkipLeadMovement(Boolean(existingTemplate.skipLeadMovement));
      const loadedBlocks = Array.isArray(existingTemplate.blocks)
        ? existingTemplate.blocks.map((b: any) => ({
            ...b,
            id: b.id || generateBlockId(),
          }))
        : [];
      setBlocks(loadedBlocks);
    }
  }, [existingTemplate]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const createMutation = useMutation({
    mutationFn: (data: any) => mailTemplatesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mail-templates'] });
      toast.success('Template criado com sucesso!');
      navigate('/campanhas?tab=templates');
    },
    onError: () => {
      toast.error('Erro ao criar template.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id: tid, data }: { id: number; data: any }) =>
      mailTemplatesService.update(tid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mail-templates'] });
      toast.success('Template salvo com sucesso!');
      navigate('/campanhas?tab=templates');
    },
    onError: () => {
      toast.error('Erro ao salvar template.');
    },
  });

  const handleSave = useCallback(() => {
    if (!name.trim()) {
      toast.error('Informe um nome para o template.');
      return;
    }

    // Strip id from blocks before sending to API
    const cleanBlocks = blocks.map(({ id: _id, ...rest }) => rest);

    const payload = {
      name: name.trim(),
      subject: subject.trim(),
      blocks: cleanBlocks,
      productId: selectedProductId,
      skipLeadMovement,
    };

    if (isNew) {
      createMutation.mutate(payload);
    } else if (templateId) {
      updateMutation.mutate({ id: templateId, data: payload });
    }
  }, [
    name,
    subject,
    skipLeadMovement,
    blocks,
    selectedProductId,
    isNew,
    templateId,
    createMutation,
    updateMutation,
  ]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    const activeData = active.data.current as any;

    // Drop from palette onto canvas
    if (activeData?.fromPalette) {
      const newBlock = createBlock(activeData.type);
      setBlocks((prev) => [...prev, newBlock]);
      setSelectedBlockId(newBlock.id);
      return;
    }

    // Reorder blocks within canvas
    if (active.id !== over.id) {
      setBlocks((prev) => {
        const oldIndex = prev.findIndex((b) => b.id === active.id);
        const newIndex = prev.findIndex((b) => b.id === over.id);
        if (oldIndex === -1 || newIndex === -1) {
          return prev;
        }
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }, []);

  const handleBlockChange = useCallback((updated: any) => {
    setBlocks((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  }, []);

  const handleRemoveBlock = useCallback((blockId: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== blockId));
    setSelectedBlockId((prev) => (prev === blockId ? null : prev));
  }, []);

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId) || null;

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (!isNew && isLoadingTemplate) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-6 h-6 animate-spin text-info" />
        <span className="ml-2 text-ink-tertiary">Carregando template...</span>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="flex flex-col h-screen bg-canvas">
        {/* Top Bar */}
        <div className="flex items-center gap-3 px-4 py-3 bg-surface border-b border-border shadow-sm flex-shrink-0">
          <button
            onClick={() => navigate('/campanhas?tab=templates')}
            className="p-1.5 rounded-lg text-ink-tertiary hover:text-ink-primary hover:bg-subtle transition-colors"
            title="Voltar para templates"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="flex-1 flex items-center gap-3 min-w-0">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome do template"
              className="flex-1 px-3 py-1.5 text-sm font-medium border border-border rounded-lg focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent min-w-0"
            />
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Assunto do e-mail"
              className="flex-1 px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent min-w-0"
            />
          </div>

          <label
            className="flex items-center gap-2 text-xs text-ink-secondary cursor-pointer select-none whitespace-nowrap"
            title="Quando marcado, enviar este template via campanha não move o lead de coluna no kanban"
          >
            <input
              type="checkbox"
              checked={skipLeadMovement}
              onChange={(e) => setSkipLeadMovement(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-accent cursor-pointer"
            />
            Não mover lead
          </label>

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-1.5 bg-info hover:opacity-90 disabled:bg-info text-white text-sm font-medium rounded-lg transition-colors"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar
          </button>
        </div>

        {/* 3-Column Layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left: Block Palette (200px) */}
          <div className="w-[200px] flex-shrink-0 bg-surface border-r border-border p-3 overflow-y-auto">
            <BlockPalette />
          </div>

          {/* Center: Block Canvas */}
          <div className="flex-1 overflow-y-auto p-4">
            <BlockCanvas
              blocks={blocks}
              selectedBlockId={selectedBlockId}
              onSelectBlock={setSelectedBlockId}
              onRemoveBlock={handleRemoveBlock}
            />
          </div>

          {/* Right: Block Editor + Email Preview (300px) */}
          <div className="w-[300px] flex-shrink-0 bg-surface border-l border-border flex flex-col overflow-hidden">
            {/* Block Editor */}
            <div className="flex-shrink-0 p-4 border-b border-border overflow-y-auto max-h-[50%]">
              <BlockEditor block={selectedBlock} onChange={handleBlockChange} theme={emailTheme} />
            </div>

            {/* Email Preview */}
            <div className="flex-1 overflow-y-auto p-4">
              <EmailPreview blocks={blocks} expandable productId={templateProductId} />
            </div>
          </div>
        </div>
      </div>
    </DndContext>
  );
}

export default MailTemplateEditorPage;
